import React from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider } from 'styled-components';
import { Provider } from 'react-redux';
import { AnalyticsProvider as EventableAnalyticsProvider } from '@carvana/eventable-clickstream';
import { Provider as AnalyticsProvider } from '@carvana/analytics';
import { ExperimentProvider, FetchExperimentData } from '@carvana/experiment';
import { JavascriptEventing } from '@Header/JavascriptEventing';

import { MfeReact, MfeReactComponent } from '@carvana/microfrontend-react';
import { MfeConfig } from '@carvana/microfrontend';
import { afterSignInSuccess } from '@utilities/afterSignInSuccess';
import getMfeCacheBuster from '@utilities/mfeCacheBuster';
import { LeadCapture } from './LeadCapture/LeadCapture';

import configureStore from '../store/ConfigureStore';
import theme from '../theme';
import Header from './Header';
import GetAnalyticsConfig from '../utilities/GetAnalyticsConfig';
import { CarvanaProviderWithRenderProp } from '../Provider';
import Cookie from '../services/Cookie';

const store = configureStore();

const loggedInSuccess = () => {
  try {
    return afterSignInSuccess();
  } catch (e) {
    console.error(e);
    return null;
  }
};

const logStepComplete = (step, ...args) => {
  if (process.env.NODE_ENV !== 'production') {
    console.log('step-complete::', step, ...args);
  }
};

const accountModalConfigDefaults = {
  CallerIdentifier: 'AccountCreationForm',
  EnterEmail: {
    onSuccess: (...args) => {
      logStepComplete('EnterEmail', ...args);
    }
  },
  SignIn: {
    onSuccess: (...args) => {
      logStepComplete('SignIn', ...args);
    }
  },
  CreateAccount: {
    onSuccess: (...args) => {
      logStepComplete('CreateAccount', ...args);
    }
  },
  GoogleOneTap: {
    enabled: false,
    onSuccess: (...args) => {
      logStepComplete('GoogleOneTap', ...args);
    }
  },
  SocialLogins: {
    onSuccess: (...args) => {
      logStepComplete('SocialLogins', ...args);
    }
  },
  ForgotPassword: {},
  ResetConfirmation: {},
  PhoneCapture: {},
  OnAuthenticatedSuccess: () => {
    loggedInSuccess();
  }
};

const SignInLinkPlaceholder = () => {
  return <span />;
};

class CarvanaHeader extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      analyticsConfig: {},
      showLcmUx: false,
      accountModalConfig: accountModalConfigDefaults
    };
  }

  setSupportDropDownPositioningStyles() {
    document.head.appendChild(
      Object.assign(document.createElement('style'), {
        textContent: `

      #support-dropdown {
        display: flex;
        max-width: 1800px;
        margin-left: auto;
        margin-right: auto;
        position: relative;
        justify-content: center;
      }

      #support-dropdown > div {
        width: auto;
        right: 0;
        left: unset;
        padding: unset;
      }

      #support-dropdown div[data-cv-test='support-dropdown'] {
       position: absolute;
       right: 0;
       top: 0;
       left: unset;
       height: 100%;
       max-width: unset;
      }
      `
      })
    );
  }

  shouldShowLeadCaptureModalExperience() {
    const { pathname } = window.location;
    const isSrpPage = () => pathname?.toLocaleLowerCase()?.includes('cars');
    const isCvtPage = () => pathname?.toLocaleLowerCase()?.includes('value-tracker');
    const shouldShowLcm = isSrpPage() || isCvtPage();

    return shouldShowLcm;
  }

  shouldEnableOneTap() {
    const { pathname } = window.location;
    const isOnlineNotaryPage = () => pathname?.toLocaleLowerCase()?.includes('remote-online-notary/msk');
    const isALCUI = () => pathname?.toLocaleLowerCase()?.includes('auto-loan-calculator');
    const isMobile = () => window.innerWidth < 768;

    // Leave Google OneTap disabled for Online Notary page
    if(isOnlineNotaryPage()) {
      return;
    } 
    
    // Fetch core-alc-onetap experiment bucketing & set Google OneTap
    if (isALCUI() && isMobile()) {
      const parsedCookies = Cookie.getAllCookies();
      const identifier = Cookie.getCookie('BrowserCookieId');
      const experimentDefaults = {
        cacheMinutes: 15,
        environment: this.props.environment,
        identifier
      };

      FetchExperimentData({'core-alc-onetap': {}}, experimentDefaults, parsedCookies).then( experimentData => {
        const {experiments, cookies} = experimentData;
        cookies.forEach(cookieToSet => document.cookie = cookieToSet);
        const enableOneTap = experiments['core-alc-onetap']?.assignedBucket === 'control' ?? true;

        this.setState({
          accountModalConfig: {
            ...this.state.accountModalConfig,
            GoogleOneTap: {
              ...this.state.accountModalConfig.GoogleOneTap,
              enabled: enableOneTap
            }
          }
        });
      });
      return;
    } 

    // DEFAULT: Enable Google OneTap for all other routes
    this.setState({
      accountModalConfig: {
        ...this.state.accountModalConfig,
        GoogleOneTap: {
          ...this.state.GoogleOneTap,
          enabled: true
        }
      }
    });
  }

  componentDidMount() {
    this.setState({
      analyticsConfig: GetAnalyticsConfig()
    });

    this.setSupportDropDownPositioningStyles();

    const mfeCacheBust = getMfeCacheBuster();

    const mfeConfigCollection = {
      name: 'header-plugin',
      Render: MfeReact,
      ENV: process.env.NODE_ENV,
      debug: process.env.LOCAL,
      globals: {
        React: () => import('react'),
        ReactDOM: () => import('react-dom'),
        styled: () => import('styled-components'),
        'styled-components': () => import('styled-components'),
        PropTypes: () => import('prop-types')
      },
      files: [
        [
          'AccountModal',
          [
            {
              chunks: ['index.css'],
              priority: 'high',
              component: 'default',
              default: {
                file: 'index.js',
                manifest: `${process.env.CVNA_APP_CDN_URL_FASTLY}/authentication-flows/account-modal/assets-manifest.json`
              }
            }
          ]
        ],
        [
          'FavoritesModule',
          [
            {
              chunks: ['styles.css'],
              component: 'default',
              default: {
                file: 'index.js',
                manifest: `${process.env.CVNA_APP_CDN_URL_STATIC_FASTLY}/favorite-module/assets-manifest.json?v=${mfeCacheBust}`
              }
            }
          ]
        ],
        [
          'CarvanaChatUiModule',
          [
            {
              component: 'default',
              default: {
                file: 'main.js',
                manifest: `${process.env.CVNA_APP_CHAT_BASE_URL}/build/assets-manifest.json?v=${mfeCacheBust}`
              }
            }
          ]
        ]
      ]
    };

    const showLcmUx = this.shouldShowLeadCaptureModalExperience();
    this.setState({
      showLcmUx
    });

    if (showLcmUx) {
      mfeConfigCollection.files.push([
        'LeadCaptureModule',
        [
          {
            chunks: ['styles.css'],
            component: 'default',
            default: {
              file: 'index.js',
              manifest: `${process.env.CVNA_APP_CDN_URL_STATIC_FASTLY}/lead-capture-module/assets-manifest.json?v=${mfeCacheBust}`
            }
          }
        ]
      ]);
    }    

    this.shouldEnableOneTap();

    MfeConfig(mfeConfigCollection);
  }

  render() {
    const { environment, useProvider, isLoggedInExternally, shouldRefreshProfile, headerContext, favorites } =
      this.props;
    const { accountModalConfig, analyticsConfig, showLcmUx } = this.state;
    const identifier = Cookie.getCookie('BrowserCookieId');
    const experimentDefaults = {
      cacheMinutes: 15,
      environment,
      identifier
    };
    return (
      <Provider store={store}>
        <ThemeProvider theme={theme}>
          <EventableAnalyticsProvider
            environment={environment}
            projectName="header"
            track="core"
            teamName="core"
          >
            <ExperimentProvider defaults={experimentDefaults}>
              <AnalyticsProvider configs={analyticsConfig}>
                <CarvanaProviderWithRenderProp
                  environment={environment}
                  useProvider={useProvider}
                  isLoggedInExternally={isLoggedInExternally}
                  shouldRefreshProfile={shouldRefreshProfile}
                  headerContext={headerContext}
                  favorites={favorites}
                  render={providerProps => (
                    <>
                      <MfeReactComponent
                        module="AccountModal"
                        component="default"
                        config={accountModalConfig}
                        Anchor={SignInLinkPlaceholder}
                      />
                      <Header {...this.props} {...providerProps} />
                      <JavascriptEventing />
                      {showLcmUx && <LeadCapture />}
                    </>
                  )}
                />
              </AnalyticsProvider>
            </ExperimentProvider>
          </EventableAnalyticsProvider>
        </ThemeProvider>
      </Provider>
    );
  }
}

CarvanaHeader.propTypes = {
  environment: PropTypes.string,
  favorites: PropTypes.shape({
    favorites: PropTypes.arrayOf(PropTypes.shape({})),
    removeFavorite: PropTypes.func
  }),
  profile: PropTypes.shape({}),
  useModalUrlForAccountLinks: PropTypes.bool,
  forceMobileLayout: PropTypes.bool,
  useProvider: PropTypes.bool,
  isLoggedInExternally: PropTypes.bool,
  shouldRefreshProfile: PropTypes.bool,
  purchaseProcessVehiclePrice: PropTypes.number,
  currentRoute: PropTypes.string,
  headerContext: PropTypes.shape({})
};

CarvanaHeader.defaultProps = {
  environment: 'development',
  favorites: {},
  profile: {},
  useModalUrlForAccountLinks: false,
  forceMobileLayout: false,
  useProvider: false,
  isLoggedInExternally: false,
  shouldRefreshProfile: false,
  purchaseProcessVehiclePrice: 0,
  currentRoute: '',
  headerContext: {
    accountModal: {},
    shouldRefreshProfile: false
  }
};

export default CarvanaHeader;
